import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { gueltigeLeistungsart, useDateien, useKommentare, useLeistungen, useLeistungsarten, useMaterial, useMe, useMeineStunden, useZeitplan, useChecklisten, usePlaene } from '../hooks/data.js';
import { RegieSheet } from '../sheets/RegieSheet.jsx';
import { StundenSheet } from '../sheets/StundenSheet.jsx';
import { useFunktionen } from '../hooks/useFunktionen.js';
import { useBegriffe } from '../hooks/useBegriffe.js';
import { load } from '../lib/storage.js';
import { useTimer } from '../state/timer.jsx';
import { adresse, dateTimeLabel, dayLabel, hours, initials, isoDate } from '../lib/format.js';
import { Icon, PlayIcon, StopIcon } from '../components/Icon.jsx';

export function Overview() {
  const { nr, auftrag, base } = useAuftragCtx();
  const hat = useFunktionen();
  const b = useBegriffe();
  // Übersicht lädt nichts aus Exxas nach – sie zeigt, was beim Ansehen der Bereiche schon geladen wurde
  const leistungen = useLeistungen(nr, { laden: false });
  const material = useMaterial(nr, { laden: false });
  const dateien = useDateien(nr, { laden: false });
  const kommentare = useKommentare(nr, { laden: false });
  const plan = useZeitplan(nr, { laden: hat('zeitplan') });
  const timer = useTimer();
  const me = useMe();
  const arten = useLeistungsarten({ laden: hat('zeit') });
  const meineWoche = useMeineStunden(isoDate(), { laden: hat('meineStunden') }); // 1 Exxas-Aufruf, 10 min gemerkt
  const heute = meineWoche.data?.tage?.find((t) => t.datum === isoDate());
  const [regie, setRegie] = useState(false);
  const listen = useChecklisten(nr, { laden: false }).data?.listen;
  const plaene = usePlaene(nr, { laden: false }).data?.listen;
  const [stunden, setStunden] = useState(false);

  /** Ein Tipp: Zeit startet mit den zuletzt verwendeten Einstellungen (Tätigkeit, Leistungsart). */
  const startNow = () => timer.start({
    nummer: nr,
    titel: auftrag.titel,
    beschreibung: load('lastTaetigkeit', 'Installation'),
    leistungsartId: gueltigeLeistungsart(arten.data, load('leistungsart', null)),
  });

  const l = leistungen.data ?? [];
  const totalH = l.reduce((s, x) => s + x.stunden, 0);
  const todayH = l.filter((x) => x.meine && x.datum?.startsWith(isoDate())).reduce((s, x) => s + x.stunden, 0);
  const files = (dateien.data ?? []).filter((f) => !f.istOrdner);
  const team = [...new Map(l.map((x) => [x.userUid, x.mitarbeiter])).values()].slice(0, 8);
  const phase = currentPhase(plan.data?.phasen ?? []);
  const termine = (plan.data?.termine ?? []).filter((t) => t.datum >= isoDate()).slice(0, 3);
  const mapUrl = `https://maps.google.com/?q=${encodeURIComponent(`${adresse(auftrag.kunde)}`)}`;
  const runningHere = timer.running && timer.nummer === nr;

  return (
    <div className="ov">
      <div className="col">
        <section className="card-dark" style={{ padding: 20 }}>
          <div className="stack">
            <span className="eyebrow on-dark-muted">{b('baustelle')}</span>
            <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{auftrag.titel}</span>
            {auftrag.kunde?.firma && <span className="on-dark-muted" style={{ fontSize: 14 }}>{auftrag.kunde.firma}</span>}
            {adresse(auftrag.kunde) && <span className="on-dark-muted" style={{ fontSize: 14 }}>{adresse(auftrag.kunde)}</span>}
          </div>
          {adresse(auftrag.kunde) && (
            <a className="btn ghost" href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Icon name="nav" size={18} stroke={2.2} />Navigation starten
            </a>
          )}
        </section>

        <div className="qa">
          {!hat('zeit') ? null : runningHere ? (
            <button className="qabtn" onClick={timer.stop} disabled={timer.busy}>
              <span className="circle" style={{ background: 'var(--stop)', color: '#fff' }}><StopIcon /></span>
              <span>Zeit stoppen</span>
            </button>
          ) : timer.running ? (
            <button className="qabtn" onClick={() => timer.switchTo({ nummer: nr, titel: auftrag.titel })} disabled={timer.busy}>
              <span className="circle" style={{ background: 'var(--accent)', color: '#fff' }}><PlayIcon size={20} /></span>
              <span>Zeit hierher</span>
            </button>
          ) : (
            <button className="qabtn" onClick={startNow} disabled={!me.data || !arten.data?.length}>
              <span className="circle" style={{ background: 'var(--accent)', color: '#fff' }}><PlayIcon size={20} /></span>
              <span>Zeit starten</span>
            </button>
          )}
          {hat('kommentare') && (
            <Link className="qabtn" to={`${base}/kommentare`}>
              <span className="circle"><Icon name="camera" size={22} /></span><span>Foto &amp; Notiz</span>
            </Link>
          )}
          {hat('kundeninfos') && (
            <Link className="qabtn" to={`${base}/kunde`}>
              <span className="circle"><Icon name="user" size={22} /></span><span>Kundeninfos</span>
            </Link>
          )}
          {hat('regie') && (
            <button className="qabtn" onClick={() => setRegie(true)}>
              <span className="circle"><Icon name="plus" size={22} /></span><span>Regieauftrag</span>
            </button>
          )}
        </div>

        {hat('uebersichtsliste') && <Link to={`${base}/checkliste`} className="card" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'var(--ink)' }}>
          <span className="circle" style={{ width: 44, height: 44, borderRadius: 22, background: 'var(--accent-soft, #E3F1EC)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="check" size={22} stroke={2.4} /></span>
          <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 800 }}>Übersichtslisten Baustelle</span>
            <span className="sub ellipsis" style={{ fontSize: 13 }}>
              {listen?.length ? listen.map((l) => l.titel).join(' · ') : 'Zonen und Anlage abhaken · Excel in den Dateien'}
            </span>
          </span>
          <Icon name="chevron" size={18} />
        </Link>}

        {hat('bewaesserungsplan') && <Link to={`${base}/plaene`} className="card" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'var(--ink)' }}>
          <span className="circle" style={{ width: 44, height: 44, borderRadius: 22, background: 'var(--accent-soft, #E3F1EC)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="clock" size={22} stroke={2.2} /></span>
          <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 800 }}>Bewässerungspläne</span>
            <span className="sub ellipsis" style={{ fontSize: 13 }}>
              {plaene?.length ? plaene.map((l) => l.titel).join(' · ') : 'Programme, Startzeiten, Laufzeiten · Excel'}
            </span>
          </span>
          <Icon name="chevron" size={18} />
        </Link>}

        {phase && (
          <section className="card">
            <div className="between">
              <span className="label">Aktuelle Phase</span>
              <Link className="linkbtn" to={`${base}/zeitplan`}>Zeitplan</Link>
            </div>
            <span style={{ fontSize: 18, fontWeight: 800 }}>{phase.titel}</span>
            <div className="bar"><div style={{ width: `${phase.fortschritt}%` }} /></div>
            <span className="sub tn">{phase.fortschritt} % · bis {dayLabel(phase.ende)}</span>
          </section>
        )}

        <div className="k4">
          {hat('zeit') && <Kpi to={`${base}/zeit-material`} label="Stunden" value={leistungen.data ? hours(totalH) : '–'} sub={leistungen.data ? `ich heute ${hours(todayH)}` : 'ansehen'} />}
          {hat('material') && <Kpi to={`${base}/zeit-material#material`} label="Material" value={material.data ? `${material.data.length} Pos.` : '–'} sub={material.data ? 'auf Auftrag' : 'ansehen'} />}
          {hat('dateien') && <Kpi to={`${base}/dateien`} label="Dateien" value={dateien.data ? String(files.length) : '–'} sub={dateien.data ? 'Pläne, Fotos, Dok.' : 'ansehen'} />}
          {hat('meineStunden') && <Kpi onClick={() => setStunden(true)} label="Mein Tag" value={heute ? hours(heute.stunden) : '–'} sub="alle Aufträge" />}
        </div>
      </div>

      <div className="col">
        {hat('zeitplan') && <section className="card list">
          <h2 className="h2" style={{ margin: '10px 0 4px' }}>Nächste Termine</h2>
          {termine.length === 0 && <p className="sub" style={{ padding: '6px 0 10px' }}>Keine Termine erfasst.</p>}
          {termine.map((t, i) => (
            <div key={i} className="listrow" style={{ alignItems: 'flex-start' }}>
              <span className="stack" style={{ width: 60, flexShrink: 0, gap: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: t.datum === isoDate() ? 'var(--stop)' : 'var(--ink)' }}>{dayLabel(t.datum)}</span>
                <span className="sub tn" style={{ fontSize: 12 }}>{t.von}</span>
              </span>
              <span className="stack" style={{ gap: 2 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{t.titel}</span>
                {t.wer && <span className="sub" style={{ fontSize: 12 }}>{t.wer}</span>}
              </span>
            </div>
          ))}
        </section>}

        {hat('kommentare') && <section className="card list">
          <div className="between" style={{ margin: '10px 0 4px' }}>
            <h2 className="h2">Neueste Kommentare</h2>
            <Link className="linkbtn" to={`${base}/kommentare`}>Alle</Link>
          </div>
          {!kommentare.data && <p className="sub" style={{ padding: '6px 0 10px' }}><Link className="linkbtn" to={`${base}/kommentare`}>Kommentare ansehen</Link></p>}
          {kommentare.data?.length === 0 && <p className="sub" style={{ padding: '6px 0 10px' }}>Noch keine Kommentare.</p>}
          {(kommentare.data ?? []).slice(0, 2).map((k) => (
            <div key={k.id} className="listrow" style={{ alignItems: 'flex-start' }}>
              <span className="avatar">{initials(k.autor)}</span>
              <span className="stack" style={{ gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{k.autor} <span className="sub" style={{ fontWeight: 600 }}>· {dateTimeLabel(k.erstelltAm)}</span></span>
                <span style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--ink-2)' }}>{k.text || 'Foto'}{k.foto && k.text ? ' (mit Foto)' : ''}</span>
              </span>
            </div>
          ))}
        </section>}

        {team.length > 0 && (
          <section className="card">
            <h2 className="h2">{b('team')} auf dem {b('auftrag')}</h2>
            <div className="wrapchips">
              {team.map((name) => (
                <span key={name} className="row" style={{ gap: 8, padding: '6px 12px 6px 6px', borderRadius: 999, background: 'var(--bg)' }}>
                  <span className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(name)}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{name}</span>
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
      {regie && <RegieSheet nr={nr} auftrag={auftrag} onClose={() => setRegie(false)} />}
      {stunden && <StundenSheet onClose={() => setStunden(false)} />}
    </div>
  );
}

function Kpi({ to, onClick, label, value, sub, loading }) {
  const Tag = to ? Link : 'button';
  return (
    <Tag to={to} onClick={onClick} className="card" style={{ gap: 4, padding: 14, textDecoration: 'none', color: 'var(--ink)', border: 'none', textAlign: 'left', font: 'inherit', cursor: 'pointer' }}>
      <span className="label" style={{ fontSize: 12, color: 'var(--muted-2)' }}>{label}</span>
      {loading ? <span className="skeleton" style={{ height: 24, width: '70%' }} /> : <span className="tn" style={{ fontSize: 20, fontWeight: 800 }}>{value}</span>}
      <span className="sub" style={{ fontSize: 12 }}>{sub}</span>
    </Tag>
  );
}

function currentPhase(phasen) {
  const today = isoDate();
  return phasen.find((p) => p.start <= today && p.ende >= today && p.fortschritt < 100)
    ?? phasen.find((p) => p.fortschritt < 100)
    ?? null;
}
